/**
 * Statistics Repository
 * 통계 데이터 액세스 계층
 */

import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import {
  DashboardStats,
  RevenueStats,
  OrganizationStats,
  OwnerTrendStats,
} from '../models';
import { formatDateKST } from '../../../utils/dateUtils';

export class StatisticsRepository {
  private baseUrl = `${API_BASE_URL}/api/v1/admin/statistics`;

  private getAuthHeaders() {
    const token = localStorage.getItem('admin_token');
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  async getDashboardStats(): Promise<DashboardStats> {
    try {
      const response = await axios.get(`${this.baseUrl}/dashboard`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      // TODO: 백엔드 /api/admin/statistics/dashboard 구현 후 이 mock 데이터 제거
      if (axios.isAxiosError(error)) {
        console.warn(
          `Statistics API not available (${error.response?.status}), returning mock data:`,
          error.message
        );
      } else {
        console.warn('Statistics API not available, returning mock data:', error);
      }

      // 임시 mock 데이터 (실제 schema에 맞춤)
      return {
        total_organizations: 0,
        pending_organizations: 0,
        approved_organizations: 0,
        rejected_organizations: 0,
        total_contacts: 0,
        pending_contacts: 0,
        in_progress_contacts: 0,
        completed_contacts: 0,
        total_subscriptions: 0,
        active_subscriptions: 0,
        expiring_soon_subscriptions: 0,
        expired_subscriptions: 0,
        monthly_revenue: 0,
        annual_revenue: 0,
        today_logins: 0,
        today_actions: 0,
      };
    }
  }

  async getRevenueStats(): Promise<RevenueStats> {
    try {
      const response = await axios.get(`${this.baseUrl}/revenue`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      // TODO: 백엔드 /api/admin/statistics/revenue 구현 후 이 mock 데이터 제거
      if (axios.isAxiosError(error)) {
        console.warn(
          `Revenue stats API not available (${error.response?.status}), returning mock data:`,
          error.message
        );
      } else {
        console.warn('Revenue stats API not available, returning mock data:', error);
      }

      return {
        current_month_revenue: 0,
        last_month_revenue: 0,
        revenue_growth_rate: 0,
        current_year_revenue: 0,
        studyroom_revenue: 0,
        academy_revenue: 0,
        active_subscription_count: 0,
        average_monthly_fee: 0,
      };
    }
  }

  async getOrganizationStats(): Promise<OrganizationStats> {
    try {
      const response = await axios.get(`${this.baseUrl}/organizations`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      // TODO: 백엔드 /api/admin/statistics/organizations 구현 후 이 mock 데이터 제거
      if (axios.isAxiosError(error)) {
        console.warn(
          `Organization stats API not available (${error.response?.status}), returning mock data:`,
          error.message
        );
      } else {
        console.warn('Organization stats API not available, returning mock data:', error);
      }

      return {
        total_organizations: 0,
        pending_count: 0,
        approved_count: 0,
        rejected_count: 0,
        studyroom_count: 0,
        academy_count: 0,
        today_signups: 0,
        this_week_signups: 0,
        this_month_signups: 0,
        approval_rate: 0,
      };
    }
  }

  async getOwnerTrendStats(): Promise<OwnerTrendStats> {
    try {
      const response = await axios.get(`${this.baseUrl}/owners/trend`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      // TODO: 백엔드 /api/admin/statistics/owners/trend 구현 후 이 mock 데이터 제거
      if (axios.isAxiosError(error)) {
        console.warn(
          `Owner trend stats API not available (${error.response?.status}), returning mock data:`,
          error.message
        );
      } else {
        console.warn('Owner trend stats API not available, returning mock data:', error);
      }

      // 최근 30일 mock 데이터 생성
      const dailySignups = [];
      const today = new Date();
      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        dailySignups.push({
          date: formatDateKST(date),
          count: Math.floor(Math.random() * 5),
        });
      }

      return {
        daily_signups: dailySignups,
        status_distribution: {
          pending: 5,
          approved: 180,
          suspended: 3,
          rejected: 12,
        },
        service_type_distribution: {
          '독서실': 120,
          '학원': 50,
          '스터디카페': 30,
        },
        key_metrics: {
          total_owners: 200,
          new_this_month: 15,
          active_owners: 180,
          churn_rate: 3.2,
        },
      };
    }
  }
}
